'use client'

import { forwardRef } from 'react'
import { IMaskInput } from 'react-imask'
import { SearchIcon } from 'lucide-react'
import { cn } from '@/utils/utils'
import { Spinner } from '@/components/ui/spinner'
import { CEP_DIGIT_LENGTH, stripCep } from '@/utils/cep'

type CepInputProps = Omit<React.ComponentProps<'input'>, 'value'> & {
  value?: string
  onAccept?: (value: string) => void
  isLoading?: boolean
  onSearch?: () => void
}

export const CepInput = forwardRef<HTMLInputElement, CepInputProps>(
  (
    {
      value,
      onAccept,
      isLoading = false,
      onSearch,
      placeholder = '00000-000',
      disabled,
      className,
      ...props
    },
    ref,
  ) => {
    const canSearch = stripCep(value).length === CEP_DIGIT_LENGTH

    return (
      <div
        className={cn(
          'flex h-9 w-full min-w-0 items-stretch overflow-hidden rounded-3xl border border-transparent bg-input/50 transition-[color,box-shadow,background-color] focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/30 disabled:pointer-events-none disabled:opacity-50',
          className,
        )}
      >
        <IMaskInput
          mask="00000-000"
          unmask
          value={value}
          onAccept={(value) => onAccept?.(value)}
          inputRef={ref}
          inputMode="numeric"
          autoComplete="postal-code"
          disabled={disabled}
          className="h-9 min-w-0 flex-1 bg-transparent px-3 py-1 text-base outline-none placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed md:text-sm"
          placeholder={placeholder}
          {...props}
        />

        <button
          type="button"
          aria-label="Buscar CEP"
          aria-busy={isLoading}
          className="flex w-9 shrink-0 items-center justify-center self-stretch border-l border-border/40 text-muted-foreground outline-none transition-colors hover:bg-input/60 hover:text-foreground focus-visible:outline-none disabled:pointer-events-none disabled:opacity-100 dark:hover:bg-input/40"
          disabled={disabled || !canSearch || isLoading}
          onClick={() => {
            if (!isLoading) onSearch?.()
          }}
        >
          {isLoading ? (
            <Spinner className="size-4" />
          ) : (
            <SearchIcon className="size-4" />
          )}
        </button>
      </div>
    )
  },
)

CepInput.displayName = 'CepInput'